
import draggable from "vuedraggable";
import { CrudCollection } from "../../CrudCollection";

export default {
  components: {
    draggable,
  },
  data() {
    return {
      loading: false,
      dialog: false,
      success: false,

      pickerDialog: false,
      mode: "selected",
      orderedCandidates: [],
    };
  },
  props: {
    collection: {
      type: CrudCollection,
      required: true,
    },
  },
  computed: {
    // The model's visible fields -- each already resolvable to a table
    // column, append, or relationship name server-side (CollectionExport's
    // own validation drops anything that isn't, logged, so an over-inclusive
    // candidate list here fails safe rather than 500ing).
    candidateFields() {
      return this.collection.model
        .getVisibleFields([], true)
        .map((field) => ({
          key: field.property.serializedName,
          label: field.label,
        }));
    },
    hasSelectedColumns() {
      return this.orderedCandidates.some((candidate) => candidate.selected);
    },
  },
  methods: {
    openPicker() {
      const selectedKeys = this.collection.fullExport
        ? []
        : this.collection.columns && this.collection.columns.length
        ? this.collection.columns
        : this.collection.defaultExportColumns;

      this.mode = this.collection.fullExport ? "full" : "selected";

      const candidatesByKey = new Map(
        this.candidateFields.map((candidate) => [candidate.key, candidate])
      );

      const selected = selectedKeys
        .filter((key) => candidatesByKey.has(key))
        .map((key) => ({ ...candidatesByKey.get(key), selected: true }));

      const selectedSet = new Set(selectedKeys);
      const rest = this.candidateFields
        .filter((candidate) => !selectedSet.has(candidate.key))
        .map((candidate) => ({ ...candidate, selected: false }));

      this.orderedCandidates = [...selected, ...rest];
      this.pickerDialog = true;
    },

    confirmAndExport() {
      const fullExport = this.mode === "full";
      const columns = fullExport
        ? null
        : this.orderedCandidates
            .filter((candidate) => candidate.selected)
            .map((candidate) => candidate.key);

      this.collection.setExportState({ columns, fullExport });
      this.pickerDialog = false;
      this.downloadExport();
    },

    downloadExport() {
      this.loading = true;

      const payload = { ...this.collection.remoteQueryValues };
      if (
        !this.collection.fullExport &&
        this.collection.columns &&
        this.collection.columns.length
      ) {
        // The querystring builder repeats a plain array key without PHP's
        // bracket suffix (columns=a&columns=b), which Laravel parses as a
        // scalar (last value wins), not an array -- crashing
        // CollectionExport::withColumns()'s ?array type hint. The bracket
        // suffix is what tells PHP's superglobal parsing to build a real
        // array; Laravel strips it back to the plain "columns" input key.
        payload["columns[]"] = this.collection.columns;
      }

      this.collection.model
        .export(payload)
        .then((data) => {
          if (this.$nuxt.$config.useQueuedExports) {
            return this.handleQueuedExportResult(data);
          } else {
            return this.handleSyncExportResult(data);
          }
        })
        // The picker dialog has already closed by the time the request
        // settles, so a swallowed rejection is indistinguishable from an
        // export still being prepared. Reuse the "Export Failed" dialog this
        // component already renders for a queued export the server declined,
        // and report at error level -- console.log is not a Sentry breadcrumb,
        // so the previous handler left no record anywhere. The request payload
        // is deliberately not logged: it carries the user's filter values,
        // which can be personal data.
        .catch((err) => {
          console.error(
            "Collection export failed:",
            {
              model: this.collection.model.typeLabelPlural,
              fullExport: this.collection.fullExport,
              columnCount: this.collection.fullExport
                ? null
                : (this.collection.columns || []).length,
              queued: !!this.$nuxt.$config.useQueuedExports,
            },
            err
          );

          this.success = false;
          this.dialog = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    handleQueuedExportResult(data) {
      this.success = data.success;
      this.dialog = true;
    },

    handleSyncExportResult(data) {
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        this.collection.model.typeLabelPlural +
          "-export-" +
          Date.now() +
          "." +
          this.$nuxt.$config.exportFormat
      );
      document.body.appendChild(link);
      link.click();
    },
  },
};
